import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from '@mypoz/i18n';
import { Icon, cn } from '@mypoz/ui';

import { useAccountSession } from '@/features/account/index.js';
import { ACCOUNT_MENU, type AccountMenuItem } from './navigation.js';

/** The menu's items in runs that share a heading (`group`), in display order. */
function groupsOf(
  items: readonly AccountMenuItem[],
): { group: AccountMenuItem['group']; items: AccountMenuItem[] }[] {
  const groups: { group: AccountMenuItem['group']; items: AccountMenuItem[] }[] = [];
  for (const item of items) {
    const last = groups.at(-1);
    if (last && last.group === item.group) last.items.push(item);
    else groups.push({ group: item.group, items: [item] });
  }
  return groups;
}

/**
 * The profile icon in the header and the menu it opens (MYP-136): My profile, the Finance centre
 * pages, Rebates & offers and Warehouse address, which left the sidebar for it.
 *
 * A menu button in the WAI-ARIA sense: the arrow keys move between the items, Home/End jump to the
 * ends, Esc closes it and hands focus back to the icon, and a click outside, Tab or choosing an
 * item closes it too. On a phone it spans the screen width with 44px rows.
 */
export function AccountMenuButton() {
  const { t: tNav } = useTranslation('nav');
  const { profile } = useAccountSession();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const items = () =>
    Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role=menuitem]') ?? []);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  };

  // Opening moves focus to the current page's item (or the first), as a menu does.
  useEffect(() => {
    if (!open) return;
    const all = items();
    (all.find((item) => item.getAttribute('aria-current') === 'page') ?? all[0])?.focus();
  }, [open]);

  // A click anywhere outside the button and the menu closes it.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Any navigation — an item, the back button — closes it.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const all = items();
    const at = all.indexOf(document.activeElement as HTMLElement);
    const move = (index: number) => {
      event.preventDefault();
      all[(index + all.length) % all.length]?.focus();
    };
    if (event.key === 'ArrowDown') move(at + 1);
    else if (event.key === 'ArrowUp') move(at - 1);
    else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(all.length - 1);
    else if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
    } else if (event.key === 'Tab') close(false);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        data-tour="header-profile"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={tNav('accountMenu')}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          'flex size-11 items-center justify-center rounded-md transition-colors hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white lg:size-9',
          open && 'bg-white/20',
        )}
      >
        <Icon name="person" size={22} />
      </button>

      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={tNav('accountMenu')}
          onKeyDown={onMenuKeyDown}
          className={cn(
            'fixed inset-x-2 top-16 z-50 overflow-hidden rounded-lg bg-surface text-ink shadow-raised ring-1 ring-black/5',
            'sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:mt-2 sm:w-72',
          )}
        >
          {/* Who is signed in — not an item, so the arrow keys skip it. */}
          <div className="flex items-center gap-3 border-b border-line px-4 py-3">
            <span
              aria-hidden="true"
              className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand"
            >
              <Icon name="person" size={20} />
            </span>
            {/* The name when the account has one; the customer ID either way. */}
            <span className="min-w-0">
              {profile?.name ? (
                <span className="block truncate text-sm font-semibold">{profile.name}</span>
              ) : null}
              <span
                className={cn(
                  'block',
                  profile?.name ? 'text-xs text-muted' : 'text-sm font-semibold',
                )}
              >
                {profile?.customerId ?? '—'}
              </span>
            </span>
          </div>

          <div className="py-1">
            {groupsOf(ACCOUNT_MENU).map(({ group, items: groupItems }) => (
              <div key={group ?? 'top'} role="group" aria-label={group ? tNav(group) : undefined}>
                {group ? (
                  <p
                    aria-hidden="true"
                    className="px-4 pt-3 pb-1 text-[11px] font-semibold tracking-[0.06em] text-muted-soft uppercase"
                  >
                    {tNav(group)}
                  </p>
                ) : null}
                {groupItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex min-h-11 items-center gap-3 border-l-[3px] px-4 text-sm transition-colors lg:min-h-10',
                        'focus-visible:bg-surface-alt focus-visible:outline-none',
                        isActive
                          ? 'border-brand bg-brand-soft font-semibold text-brand-dark'
                          : 'border-transparent hover:bg-surface-alt',
                      )
                    }
                  >
                    <Icon name={item.icon} size={19} className="text-muted" />
                    <span className="min-w-0 flex-1">{tNav(item.key)}</span>
                  </NavLink>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The "My account" menu on each account page (MYP-136): a list beside the page on a laptop, a row
 * of tabs under the header on a phone, so one account page leads to the next without going back
 * to the header.
 */
export function AccountSectionNav() {
  const { t: tNav } = useTranslation('nav');

  return (
    <nav aria-label={tNav('myAccount')} className="mb-5 lg:mb-0">
      <p className="mb-2 hidden px-3 text-[11px] font-semibold tracking-[0.06em] text-muted-soft uppercase lg:block">
        {tNav('myAccount')}
      </p>
      <ul className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:-mx-6 sm:px-6 lg:mx-0 lg:grid lg:gap-1 lg:overflow-visible lg:px-0 lg:pb-0">
        {ACCOUNT_MENU.map((item) => (
          <li key={item.to} className="shrink-0">
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex min-h-11 items-center gap-2.5 rounded-full border px-4 text-sm whitespace-nowrap transition-colors',
                  'lg:min-h-10 lg:rounded-md lg:border-0 lg:px-3',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
                  isActive
                    ? 'border-brand bg-brand-soft font-semibold text-brand-dark'
                    : 'border-line bg-surface text-ink hover:bg-surface-alt lg:bg-transparent',
                )
              }
            >
              <Icon name={item.icon} size={18} className="hidden text-muted lg:block" />
              {tNav(item.key)}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
